import React, { useState } from 'react';
import { FiGithub, FiExternalLink, FiStar, FiUser, FiCheckCircle, FiXCircle, FiAlertCircle, FiVideo } from 'react-icons/fi';

const ProjectCard = ({ 
  project, 
  judgeName, 
  onScreening, 
  onEvaluate, 
  onHistory 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Check if current judge has voted/screened
  const hasJudgeVoted = project.feedbackList?.some(f => f.judge_name === judgeName);
  const isQualified = project.screeningStatus === 'qualified';
  const isDisqualified = project.screeningStatus === 'disqualified';
  const isPending = project.screeningStatus === 'pending';

  const shouldShowExpand = project.short_description && project.short_description.length > 150;
  const description = project.short_description || '';

  return (
    <div className={`bg-dark-blue border-2 ${isDisqualified ? 'border-red-500/30 opacity-75' : isQualified ? 'border-green-500' : 'border-yellow'} rounded-2xl overflow-hidden hover:border-white/50 transition-all duration-300 relative group h-full flex flex-col`}>
      {/* Status Badge */}
      <div className="absolute top-4 left-4 z-10 flex gap-2">
        {isQualified && (
          <div className="bg-green-500 text-white px-3 py-1 rounded-full font-bold text-sm shadow-lg flex items-center gap-1">
            <FiCheckCircle /> مؤهل
          </div>
        )}
        {isDisqualified && (
          <div className="bg-red-500 text-white px-3 py-1 rounded-full font-bold text-sm shadow-lg flex items-center gap-1">
            <FiXCircle /> مستبعد
          </div>
        )}
        {isPending && (
          <div className="bg-yellow text-dark-blue px-3 py-1 rounded-full font-bold text-sm shadow-lg flex items-center gap-1">
            <FiAlertCircle /> قيد التصفية
          </div>
        )}
      </div>

      <div className={`bg-white/5 px-6 py-4 border-b ${isDisqualified ? 'border-red-500/20' : 'border-white/10'}`}>
        <div className="flex items-start justify-between pr-12">
          <div>
            <h3 className="text-xl font-bold text-white mb-1">{project.team?.team_name || 'بدون فريق'}</h3>
            <p className="text-yellow text-sm">
              {project.team?.leader?.full_name || 'غير محدد'}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4 flex-1 flex flex-col">
        <div>
          <p className="text-xs text-white/50 mb-2">وصف المشروع</p>
          <div className="relative">
            <p className={`text-sm text-white/80 leading-relaxed transition-all ${!isExpanded && shouldShowExpand ? 'line-clamp-3' : ''}`}>
              {description}
            </p>
            {shouldShowExpand && (
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-yellow text-xs mt-1 hover:underline focus:outline-none"
              >
                {isExpanded ? 'عرض أقل' : 'عرض المزيد'}
              </button>
            )}
          </div>
        </div>

        {/* Score Display - Only if Qualified */}
        {isQualified && (
          <div className="flex items-center gap-4 py-3 border-y border-white/10 mt-auto">
            <div className="flex-1 text-center border-l border-white/10 pl-4">
              <p className="text-xs text-white/50 mb-1">متوسط التقييم</p>
              <div className="flex items-center justify-center gap-1 text-yellow font-bold text-xl">
                <FiStar className="fill-yellow" />
                <span>{project.averageScore ? project.averageScore.toFixed(1) : '-'}</span>
              </div>
            </div>
            <div className="flex-1 text-center pr-4">
              <p className="text-xs text-white/50 mb-1">عدد المقيمين</p>
              <div className="flex items-center justify-center gap-1 text-white font-bold text-xl">
                <FiUser />
                <span>{project.feedbackCount || 0}</span>
              </div>
            </div>
          </div>
        )}

        {/* Screening Status Stats */}
        <div className="flex gap-4 text-xs text-white/50 bg-white/5 p-2 rounded-lg mt-auto">
          <div className="flex items-center gap-1">
            <FiCheckCircle className="text-green-500" />
            <span>مطابق: {project.themeMatchVotes || 0}</span>
          </div>
          <div className="flex items-center gap-1">
            <FiXCircle className="text-red-500" />
            <span>غير مطابق: {project.themeMismatchVotes || 0}</span>
          </div>
        </div>

        {/* Links */}
        <div className="space-y-2 mt-auto">
          {project.github_url && (
            <a href={project.github_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/80 hover:text-yellow transition-colors">
              <FiGithub size={16} /><span className="truncate">Github</span><FiExternalLink size={12} className="mr-auto" />
            </a>
          )}
          {project.demo_url && (
            <a href={project.demo_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/80 hover:text-yellow transition-colors">
              <FiExternalLink size={16} /><span className="truncate">رابط الديمو</span><FiExternalLink size={12} className="mr-auto" />
            </a>
          )}
          {project.additional_files && (
            <a href={project.additional_files} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/80 hover:text-yellow transition-colors">
              <FiVideo size={16} /><span className="truncate">الفيديو</span><FiExternalLink size={12} className="mr-auto" />
            </a>
          )}
        </div>

        <div className="pt-4 mt-4 flex flex-col gap-2">
          {/* Action Buttons Logic */}
          {isPending && !hasJudgeVoted && (
            <button
              onClick={() => onScreening(project)}
              className="w-full py-3 bg-yellow text-dark-blue rounded-xl font-bold hover:bg-yellow/90 transition-all flex items-center justify-center gap-2"
            >
              <FiCheckCircle /> تصفية أولية
            </button>
          )}

          {isPending && hasJudgeVoted && (
            <div className="w-full py-3 bg-white/10 text-white/50 rounded-xl font-bold text-center border border-dashed border-white/20">
              تم التصويت (بانتظار البقية)
            </div>
          )}

          {isQualified && (
            <div className="flex gap-2">
              <button
                onClick={() => onHistory(project)}
                className="flex-1 py-3 bg-blue/30 text-white border border-yellow/20 rounded-xl font-bold hover:bg-blue/40 transition-all text-sm"
              >
                التقييمات
              </button>
              <button
                onClick={() => onEvaluate(project)}
                className="flex-1 py-3 bg-yellow text-dark-blue rounded-xl font-bold hover:bg-yellow/90 transition-all flex items-center justify-center gap-2"
              >
                <FiStar /> تقييم
              </button>
            </div>
          )}

          {isDisqualified && (
            <div className="w-full py-3 bg-red-500/10 text-red-500 rounded-xl font-bold text-center border border-red-500/20">
              تم استبعاد المشروع
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;

